import React from 'react';
import clsx from 'clsx';
import { useLoaderData } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { ArrowDownIcon } from '@heroicons/react/20/solid';
import { Spinner } from '@oysterjs/ui/spinner';
import { Await } from '@oysterjs/ui/async';
import { AnimateIn } from '@oysterjs/ui/transition';

import {
  Action,
  type CaraWorkspaceFieldsFragment,
  CaraWorkspaceSchemaVersion,
  CaraWorkspaceState,
  type CustomFormListFieldsFragment,
  type HtmlDocumentTemplateFieldsFragment,
  type LoggedInAgencyUserFieldsFragment,
  type ProducerInformationFieldsFragment,
  type WordDocumentTemplateFieldsFragment,
  type ExcelSpreadsheetTemplateFieldsFragment,
  Resource
} from '~/api/graphql';
import { useDesktopNotifications } from '~/lib/useDesktopNotifications';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { PermissionNotice, usePermissions } from '~/components/permissions';
import { CaraInput } from '~/components/cara-input/cara-input';
import { WorkspaceEffortLevelBadge } from '~/components/workspaces/workspace-effort-level-badge';
import {
  customFormPickerOptions,
  excelTemplatePickerOptions,
  htmlTemplatePickerOptions,
  wordTemplatePickerOptions
} from '~/components/cara-input/template-picker-options';

import { ArtifactFocusDialog } from '~/components/workspaces/artifact-focus';
import { ArtifactPanel } from '~/components/workspaces/artifact-panel';
import { usePreloadTemplateFormEditor } from '~/components/workspaces/artifact-focus-template-form';
import { Conversation } from '~/components/workspaces/conversation';
import { WorkspaceTopBar } from '~/components/workspaces/workspace-top-bar';
import { useAddCaraWorkspaceMessage } from '~/components/workspaces/hooks/use-add-cara-workspace-message';
import { useArtifactFocus } from '~/components/workspaces/hooks/use-artifact-focus';
import { useArtifactPanelWidth } from '~/components/workspaces/hooks/use-artifact-panel-width';
import { useCaraWorkspaceSubscription } from '~/api/data/workspaces/use-cara-workspace-subscription';
import { useConvertDocumentToTemplate } from '~/components/workspaces/hooks/use-convert-document-to-template';
import { useStopCaraWorkspace } from '~/components/workspaces/hooks/use-stop-cara-workspace';
import { useJumpButtons } from '~/components/workspaces/hooks/use-jump-buttons';
import { useUrlArtifactSync } from '~/components/workspaces/hooks/use-url-artifact-sync';
import { useFollowCreatedVersion } from '~/components/workspaces/hooks/use-follow-created-version';
import { WorkspaceOutlinePanel } from '~/components/workspaces/workspace-outline';
import { withSavedArtifact } from '~/components/workspaces/lib/artifact-versions';

type LoaderData = Partial<Omit<CopilotWorkspacePageInnerProps, 'workspace'>> & {
  caraWorkspace?: CopilotWorkspacePageInnerProps['workspace'];
};

export const CopilotWorkspacePage = () => {
  const loader = useLoaderData() as { data: Promise<LoaderData> } | undefined;

  return (
    <React.Suspense fallback={<Spinner size="size-8" color="neutral" />}>
      <Await resolve={loader?.data}>
        {(data) =>
          data?.caraWorkspace && (
            <CopilotWorkspacePageInner
              agencyProducerInformations={data.agencyProducerInformations ?? []}
              agencyUser={data.agencyUser}
              customForms={data.customForms ?? []}
              htmlDocumentTemplates={data.htmlDocumentTemplates || []}
              userProducerInformations={data.userProducerInformations ?? []}
              wordDocumentTemplates={data.wordDocumentTemplates ?? []}
              excelSpreadsheetTemplates={data.excelSpreadsheetTemplates ?? []}
              workspace={data.caraWorkspace}
            />
          )
        }
      </Await>
    </React.Suspense>
  );
};

export interface CopilotWorkspacePageInnerProps {
  agencyProducerInformations: ProducerInformationFieldsFragment[];
  agencyUser?: LoggedInAgencyUserFieldsFragment;
  customForms: CustomFormListFieldsFragment[];
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[];
  userProducerInformations: ProducerInformationFieldsFragment[];
  wordDocumentTemplates: WordDocumentTemplateFieldsFragment[];
  excelSpreadsheetTemplates: ExcelSpreadsheetTemplateFieldsFragment[];
  workspace: CaraWorkspaceFieldsFragment;
}

const UPDATE_CARA_WORKSPACE = [{ resource: Resource.CaraWorkspace, action: Action.Update }];

/**
 * Positions a jump button over the conversation. Below `lg` the page itself
 * scrolls, so the button is fixed to the viewport; from `lg` up it is absolute
 * within the conversation column so it stays centred on the conversation when
 * the artifact panel takes over part of the viewport. `inset-x-0` resolves
 * against the right box either way.
 *
 * The slot owns the centring so the button is free to animate `y` without its
 * inline transform clobbering a `-translate-x-1/2` class.
 */
const JumpButtonSlot = (props: {
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) => (
  <div
    className={clsx(
      'flex fixed lg:absolute inset-x-0 z-10 justify-center pointer-events-none',
      props.className
    )}
    style={props.style}
  >
    {props.children}
  </div>
);

const JumpButton = (props: {
  label: string;
  offsetY: number;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <motion.button
    type="button"
    aria-label={props.label}
    initial={{ opacity: 0, y: props.offsetY }}
    animate={{ opacity: 1, y: 0, transition: { duration: 0.2 } }}
    exit={{ opacity: 0, y: props.offsetY }}
    onClick={props.onClick}
    className="pointer-events-auto flex items-center justify-center rounded-full p-2 bg-white dark:bg-neutral-800 shadow cursor-pointer hover:shadow-lg transition-shadow duration-150"
  >
    {props.children}
  </motion.button>
);

export const CopilotWorkspacePageInner = (props: CopilotWorkspacePageInnerProps) => {
  const { workspace, setWorkspace } = useCaraWorkspaceSubscription(props.workspace);
  useDesktopNotifications(workspace, props.agencyUser?.preferences.desktopNotifications);

  // Every way of moving a workspace forward -- a follow-up message, stopping a
  // run, renaming, archiving -- is an update on the workspace itself.
  const canUpdate = usePermissions().hasAllPermissions(UPDATE_CARA_WORKSPACE);

  const send = useAddCaraWorkspaceMessage({ workspace, setWorkspace });
  const stop = useStopCaraWorkspace({ workspace, setWorkspace });
  const smartFillEnabled = useFlag(FEATURE_FLAGS.SMART_FILL);
  const focus = useArtifactFocus(workspace);
  usePreloadTemplateFormEditor(workspace);
  const convert = useConvertDocumentToTemplate({
    workspace,
    addCaraWorkspaceMessage: send.addCaraWorkspaceMessage,
    setCurrentArtifact: focus.setCurrentArtifact
  });
  useUrlArtifactSync({
    workspace,
    openArtifactFocusDialogUnchecked: focus.openArtifactFocusDialogUnchecked,
    startConvertFromArtifactId: convert.startConvertFromArtifactId,
    markUrlConvertProcessed: convert.markUrlConvertProcessed
  });
  const followCreatedVersion = useFollowCreatedVersion();

  const customFormOptionsForChat = customFormPickerOptions(props.customForms);
  const htmlTemplateOptionsForChat = htmlTemplatePickerOptions(props.htmlDocumentTemplates);
  const wordTemplateOptionsForChat = wordTemplatePickerOptions(props.wordDocumentTemplates);
  const excelTemplateOptionsForChat = excelTemplatePickerOptions(props.excelSpreadsheetTemplates);

  const jump = useJumpButtons();
  const rowRef = React.useRef<HTMLDivElement>(null);
  const panel = useArtifactPanelWidth(rowRef);

  return (
    <div className="-m-12 lg:h-screen lg:flex lg:flex-col lg:overflow-hidden">
      <WorkspaceTopBar
        workspace={workspace}
        setWorkspace={setWorkspace}
        canUpdate={canUpdate}
        onStop={stop.stopCaraWorkspace}
        stopLoading={stop.stopLoading}
      />
      <div className="flex flex-row lg:flex-1 lg:min-h-0" ref={rowRef}>
        <WorkspaceOutlinePanel
          workspace={workspace}
          overlayConversation={focus.showArtifactFocusDialog && !panel.railFits}
          onOpenArtifact={focus.requestOpenArtifact}
        />
        <div className="isolate relative flex-1 min-w-0 lg:flex lg:flex-col lg:overflow-hidden">
          <JumpButtonSlot style={{ bottom: jump.bottomOffset }}>
            <AnimatePresence>
              {jump.showJumpToBottom && (
                <JumpButton
                  key="jump-to-bottom"
                  label="Jump to the latest message"
                  offsetY={-6}
                  onClick={() => jump.jumpTo('bottom')}
                >
                  <ArrowDownIcon className="size-4 text-neutral-900 dark:text-white" />
                </JumpButton>
              )}
            </AnimatePresence>
          </JumpButtonSlot>
          <div
            className="flex flex-col lg:flex-1 lg:min-h-0 lg:overflow-y-auto"
            ref={jump.containerRef}
          >
            <div
              className="isolate flex-1 w-full max-w-4xl mx-auto px-8 mt-8 lg:mt-12"
              ref={jump.conversationRef}
            >
              <div className="w-full flex flex-col gap-4">
                {workspace.schemaVersion === CaraWorkspaceSchemaVersion.V0 ? (
                  <div className="text-sm text-neutral-700 dark:text-neutral-300 font-medium text-center">
                    This workspace has been archived. Please create a new workspace to continue.
                  </div>
                ) : (
                  <Conversation
                    workspace={workspace}
                    htmlDocumentTemplates={props.htmlDocumentTemplates}
                    onOpenArtifact={focus.requestOpenArtifact}
                    onSelectFollowUp={
                      canUpdate
                        ? (followUp) => send.addCaraWorkspaceMessage('', [], followUp)
                        : undefined
                    }
                  />
                )}
                <motion.div
                  animate={{
                    height: workspace.state === CaraWorkspaceState.Working ? '240px' : 0,
                    opacity: workspace.state === CaraWorkspaceState.Working ? 1 : 0
                  }}
                  initial={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                />
              </div>
            </div>
            <div className="sticky bottom-0 w-full" data-workspace-composer ref={jump.composerRef}>
              <div className="w-full max-w-4xl mx-auto px-4 bg-neutral-50 dark:bg-neutral-900">
                <AnimateIn className="pb-8">
                  {canUpdate ? (
                    <CaraInput
                      smartFillWorkspaceId={smartFillEnabled ? workspace.id : undefined}
                      onSubmit={send.addCaraWorkspaceMessage}
                      inline
                      loading={workspace?.state === CaraWorkspaceState.Working}
                      error={send.addMessageError}
                      onAttachmentsChanged={send.clearAddMessageError}
                      onStop={
                        workspace?.schemaVersion === CaraWorkspaceSchemaVersion.V2
                          ? stop.stopCaraWorkspace
                          : undefined
                      }
                      stopLoading={stop.stopLoading}
                      customFormPicker={{ options: customFormOptionsForChat }}
                      htmlTemplatePicker={
                        htmlTemplateOptionsForChat.length > 0
                          ? { options: htmlTemplateOptionsForChat }
                          : undefined
                      }
                      wordTemplatePicker={{ options: wordTemplateOptionsForChat }}
                      excelTemplatePicker={{ options: excelTemplateOptionsForChat }}
                      enableAmsDocumentPicker
                      effortBadge={<WorkspaceEffortLevelBadge workspace={workspace} />}
                    />
                  ) : (
                    <PermissionNotice className="mb-8">
                      You can read this workspace but not continue it. Ask an agency admin for
                      access to work with Cara.
                    </PermissionNotice>
                  )}
                </AnimateIn>
              </div>
            </div>
          </div>
        </div>
        <AnimatePresence initial={false}>
          {focus.showArtifactFocusDialog && focus.currentArtifact && (
            <ArtifactPanel
              key="artifact-panel"
              width={panel.width}
              bounds={panel.bounds}
              onResize={panel.preview}
              onResizeEnd={panel.commit}
              onResetWidth={panel.reset}
            >
              <ArtifactFocusDialog
                canUpdate={canUpdate}
                workspace={workspace}
                onClose={focus.closeArtifactFocusDialog}
                artifact={focus.currentArtifact}
                onVersionChange={focus.setCurrentArtifact}
                onVersionCreated={(created) => {
                  followCreatedVersion(created);
                  focus.showArtifactVersion(created);
                }}
                onArtifactSaved={(saved) =>
                  setWorkspace((current) => withSavedArtifact(current, saved))
                }
                htmlDocumentTemplates={props.htmlDocumentTemplates}
                agencyProducerInformations={props.agencyProducerInformations}
                userProducerInformations={props.userProducerInformations}
                emailBodyHydrating={focus.emailBodyHydrating}
                onConvertToTemplate={() =>
                  focus.currentArtifact && convert.startConvertFromArtifactId(focus.currentArtifact)
                }
                isConvertingToTemplate={convert.isConvertingToTemplate}
                autoOpenSaveDialog={convert.autoOpenSaveDialog}
                onSaveDialogClosed={() => convert.setAutoOpenSaveDialog(false)}
                onSaveStatusChange={focus.onSaveStatusChange}
                onUnsavedChangesChange={focus.onUnsavedChangesChange}
              />
            </ArtifactPanel>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
